import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  Loader2,
  Check,
  Download,
  PlayCircle,
  Clock,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { toast } from "sonner";

const ModuleDetail = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user, login, loading: authLoading } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [markingLesson, setMarkingLesson] = useState(null);
  const videoRef = useRef(null);

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/members/modules/${moduleId}`, {
        withCredentials: true,
      });
      setModule(response.data.module);
      // Auto-select first lesson
      if (response.data.module.lessons.length > 0 && !currentLesson) {
        setCurrentLesson(response.data.module.lessons[0]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        login();
      } else {
        toast.error("Module not available");
        navigate("/members");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) fetchModule();
    // eslint-disable-next-line
  }, [moduleId, authLoading]);

  const toggleLessonComplete = async (lesson) => {
    if (!user) {
      login();
      return;
    }
    setMarkingLesson(lesson.id);
    try {
      const response = await axios.post(
        `${API}/members/progress`,
        {
          module_id: moduleId,
          lesson_id: lesson.id,
          completed: !lesson.completed,
        },
        { withCredentials: true }
      );
      // Update local state
      setModule((prev) => ({
        ...prev,
        lessons: prev.lessons.map((l) =>
          l.id === lesson.id ? { ...l, completed: response.data.completed } : l
        ),
        lessons_completed:
          prev.lessons_completed +
          (response.data.completed ? 1 : -1),
      }));
      if (currentLesson?.id === lesson.id) {
        setCurrentLesson({ ...lesson, completed: response.data.completed });
      }
      toast.success(response.data.completed ? "Marked complete!" : "Marked incomplete");
    } catch (error) {
      toast.error("Couldn't update progress");
    } finally {
      setMarkingLesson(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (!module) return null;

  const progressPct = Math.round(
    (module.lessons_completed / module.lessons_total) * 100
  );

  return (
    <div
      className="min-h-screen bg-[#F9F8F6] pt-28 pb-20"
      data-testid="module-detail-page"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Back link */}
        <Link
          to="/members"
          className="inline-flex items-center gap-2 text-[#4A5543] hover:text-[#C5A059] text-sm font-light mb-8"
          data-testid="back-to-library"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Library
        </Link>

        {/* Module Header */}
        <div className="mb-10">
          <div className="flex items-baseline gap-4 mb-2">
            <span className="font-heading text-2xl text-[#C5A059] italic">
              {module.number}
            </span>
            <span className="overline">{module.subtitle}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#2C3524] leading-tight mb-4">
            {module.title}
          </h1>
          <p className="text-[#4A5543] font-light max-w-2xl leading-relaxed mb-6">
            {module.description}
          </p>

          {/* Progress bar */}
          <div className="max-w-md">
            <div className="flex justify-between text-xs text-[#4A5543]/70 tracking-wider uppercase mb-2">
              <span>
                {module.lessons_completed} of {module.lessons_total} lessons
              </span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-[#E3DAC9] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#C5A059] to-[#DCC286] transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Layout: Video + Sidebar */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content: Video Player */}
          <div className="lg:col-span-2">
            {currentLesson && (
              <div>
                <div className="relative aspect-video rounded-2xl overflow-hidden bg-[#2C3524] shadow-xl">
                  <video
                    ref={videoRef}
                    key={currentLesson.id}
                    src={currentLesson.video_url}
                    controls
                    className="w-full h-full"
                    data-testid="lesson-video"
                  />
                </div>
                <div className="mt-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <h2 className="font-heading text-2xl md:text-3xl text-[#2C3524]">
                      {currentLesson.title}
                    </h2>
                    <span className="text-xs text-[#4A5543]/70 tracking-wider uppercase whitespace-nowrap flex items-center gap-1 mt-2">
                      <Clock className="w-3 h-3" />
                      {currentLesson.duration}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => toggleLessonComplete(currentLesson)}
                      disabled={markingLesson === currentLesson.id}
                      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                        currentLesson.completed
                          ? "bg-green-100 text-green-700 border border-green-200"
                          : "bg-[#C5A059] text-white hover:bg-[#DCC286]"
                      }`}
                      data-testid="mark-complete-btn"
                    >
                      {markingLesson === currentLesson.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : currentLesson.completed ? (
                        <>
                          <Check className="w-4 h-4" />
                          Completed
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          Mark as Complete
                        </>
                      )}
                    </button>
                    <a
                      href={currentLesson.workbook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium border border-[#E3DAC9] text-[#2C3524] hover:border-[#C5A059] transition-all"
                      data-testid="download-workbook-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download Workbook
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar: Lesson list */}
          <div className="lg:col-span-1">
            <div className="sticky top-28">
              <h3 className="overline mb-4">Lessons</h3>
              <ul className="space-y-2">
                {module.lessons.map((lesson) => (
                  <li key={lesson.id}>
                    <button
                      onClick={() => setCurrentLesson(lesson)}
                      className={`w-full text-left p-4 rounded-xl transition-all border ${
                        currentLesson?.id === lesson.id
                          ? "bg-white border-[#C5A059] shadow-sm"
                          : "bg-white border-[#E3DAC9] hover:border-[#C5A059]/60"
                      }`}
                      data-testid={`lesson-${lesson.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {lesson.completed ? (
                          <CheckCircle2
                            className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5"
                            strokeWidth={2}
                          />
                        ) : (
                          <Circle
                            className="w-5 h-5 text-[#E3DAC9] flex-shrink-0 mt-0.5"
                            strokeWidth={1.5}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[#2C3524] font-medium text-sm leading-snug">
                            {lesson.title}
                          </p>
                          <p className="text-[#4A5543]/60 text-xs mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {lesson.duration}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleDetail;
